// src/pages/StarRec/businesses/Approve.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../../api/apiConfig";
import {
  getPendingBusinesses,
  approveBusiness,
  rejectBusiness,
  testConnection,
} from "../../../api/business/approveApi";

/* -------------------- MESSAGE BAR -------------------- */
function MessageBar({ message, type = "info", onClose }) {
  if (!message) return null;

  const alertClass =
    type === "error" ? "alert-danger" : type === "success" ? "alert-success" : "alert-info";

  return (
    <div className={`alert ${alertClass} d-flex justify-content-between align-items-center`}>
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- MODAL -------------------- */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* -------------------- BUSINESS FORM -------------------- */
function BusinessForm({ value, onChange, countryOptions, stateOptions, cityOptions, mode = "edit" }) {
  const disabled = mode !== "edit";
  const on = (key) => (e) => onChange({ ...value, [key]: e.target.value });

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">
          Business Name <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          value={value.businessName || ""}
          onChange={on("businessName")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Branch Name <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          value={value.branchName || ""}
          onChange={on("branchName")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-4">
        <label className="form-label">
          Country <span className="text-danger">*</span>
        </label>
        <select
          className="form-select"
          value={value.country || ""}
          onChange={(e) => onChange({ ...value, country: e.target.value, state: "", city: "" })}
          disabled={disabled}
        >
          <option value="">Select</option>
          {countryOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-4">
        <label className="form-label">
          State <span className="text-danger">*</span>
        </label>
        <select
          className="form-select"
          value={value.state || ""}
          onChange={(e) => onChange({ ...value, state: e.target.value, city: "" })}
          disabled={disabled || !value.country}
        >
          <option value="">Select</option>
          {stateOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-4">
        <label className="form-label">
          City <span className="text-danger">*</span>
        </label>
        <select
          className="form-select"
          value={value.city || ""}
          onChange={on("city")}
          disabled={disabled || !value.state}
        >
          <option value="">Select</option>
          {cityOptions.map((ct) => (
            <option key={ct} value={ct}>
              {ct}
            </option>
          ))}
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label">Business Entity</label>
        <input
          className="form-control"
          value={value.businessEntity || ""}
          onChange={on("businessEntity")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Registration Number</label>
        <input
          className="form-control"
          value={value.registrationNumber || ""}
          onChange={on("registrationNumber")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Business Category</label>
        <input
          className="form-control"
          value={value.businessCategory || ""}
          onChange={on("businessCategory")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Total Employees <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          type="number"
          min="0"
          value={value.totalEmployees ?? ""}
          onChange={on("totalEmployees")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Contact Person</label>
        <input
          className="form-control"
          value={value.contactPersonName || ""}
          onChange={on("contactPersonName")}
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Email</label>
        <input className="form-control" value={value.email || ""} onChange={on("email")} disabled={disabled} />
      </div>

      <div className="col-md-6">
        <label className="form-label">Mobile</label>
        <input
          className="form-control"
          value={value.mobileNumber || ""}
          onChange={on("mobileNumber")}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

const emptyForm = {
  businessName: "",
  country: "",
  state: "",
  city: "",
  branchName: "",
  businessEntity: "",
  registrationNumber: "",
  businessCategory: "",
  totalEmployees: "",
  contactPersonName: "",
  email: "",
  mobileNumber: "",
};

// Demo options (replace with real dynamic lists if you have APIs)
const countryOptions = ["India", "UAE", "USA"];
const stateMap = {
  India: ["Telangana", "Andhra Pradesh", "Karnataka"],
  UAE: ["Dubai", "Abu Dhabi"],
  USA: ["California", "Texas"],
};
const cityMap = {
  Telangana: ["Hyderabad", "Warangal"],
  "Andhra Pradesh": ["Vijayawada", "Visakhapatnam"],
  Karnataka: ["Bengaluru"],
  Dubai: ["Dubai City"],
  "Abu Dhabi": ["Abu Dhabi City"],
  California: ["Los Angeles", "San Francisco"],
  Texas: ["Austin", "Dallas"],
};

export default function Approve() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!authAPI.isAuthenticated()) navigate("/");
  }, [navigate]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [message, setMessage] = useState({ text: "", type: "info" });

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formDraft, setFormDraft] = useState(emptyForm);

  const stateOptions = useMemo(() => stateMap[formDraft.country] || [], [formDraft.country]);
  const cityOptions = useMemo(() => cityMap[formDraft.state] || [], [formDraft.state]);

  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "info" }), 3000);
  }, []);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPendingBusinesses();
      setRows(data);
      showMessage(data.length ? `Loaded ${data.length} pending businesses` : "No pending businesses found", data.length ? "success" : "info");
    } catch (e) {
      showMessage(e.message || "Failed to load pending businesses", "error");
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    document.title = "StarRec Approve Businesses";
    fetchPending();
  }, [fetchPending]);

  const openView = useCallback((row) => {
    const id = row._id || row.id;
    setSelectedId(id);
    const details = row.details || row;
    setFormDraft({ ...emptyForm, ...details });
    setViewOpen(true);
  }, []);

  const closeView = useCallback(() => {
    setViewOpen(false);
    setSelectedId(null);
    setFormDraft(emptyForm);
  }, []);

  const validateForm = useCallback(() => {
    const errors = [];
    if (!formDraft.businessName?.trim()) errors.push("Business Name is required");
    if (!formDraft.branchName?.trim()) errors.push("Branch Name is required");
    if (!formDraft.country) errors.push("Country is required");
    if (!formDraft.state) errors.push("State is required");
    if (!formDraft.city) errors.push("City is required");
    if (formDraft.totalEmployees === "" || formDraft.totalEmployees === null || formDraft.totalEmployees === undefined)
      errors.push("Total Employees is required");
    if (formDraft.email && !/\S+@\S+\.\S+/.test(formDraft.email)) errors.push("Invalid email format");
    return errors;
  }, [formDraft]);

  const handleApprove = useCallback(async () => {
    const errors = validateForm();
    if (errors.length) return showMessage(errors.join(", "), "error");
    if (!selectedId) return showMessage("No business selected", "error");

    if (!window.confirm("Approve this business?")) return;

    setProcessing(true);
    try {
      await approveBusiness(selectedId, formDraft);
      setRows((prev) => prev.filter((r) => (r._id || r.id) !== selectedId));
      closeView();
      showMessage("Business approved successfully!", "success");
    } catch (e) {
      showMessage(e.message || "Failed to approve business", "error");
    } finally {
      setProcessing(false);
    }
  }, [selectedId, formDraft, validateForm, showMessage, closeView]);

  const handleReject = useCallback(async () => {
    if (!selectedId) return showMessage("No business selected", "error");

    const reason = prompt("Reason for rejection:", "Rejected by admin");
    if (reason === null) return;

    if (!window.confirm("Reject this business?")) return;

    setProcessing(true);
    try {
      await rejectBusiness(selectedId, reason);
      setRows((prev) => prev.filter((r) => (r._id || r.id) !== selectedId));
      closeView();
      showMessage("Business rejected successfully!", "success");
    } catch (e) {
      showMessage(e.message || "Failed to reject business", "error");
    } finally {
      setProcessing(false);
    }
  }, [selectedId, showMessage, closeView]);

  const testApi = useCallback(async () => {
    try {
      const res = await testConnection();
      const count = Array.isArray(res) ? res.length : Array.isArray(res?.data) ? res.data.length : "data";
      showMessage(`API Connected ✅ (${count})`, "success");
    } catch (e) {
      showMessage(`API Connection Failed: ${e.message}`, "error");
    }
  }, [showMessage]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="fs-4 fw-bold m-0">Process Businesses</h1>
        <button className="btn btn-sm btn-outline-primary" onClick={testApi} disabled={processing}>
          Test API Connection
        </button>
      </div>

      <MessageBar message={message.text} type={message.type} onClose={() => setMessage({ text: "", type: "info" })} />

      {loading ? (
        <div className="text-center my-4">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading pending businesses...</p>
        </div>
      ) : null}

      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Branch Name</th>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th>Total Employees</th>
              <th>Contact Email</th>
              <th style={{ width: 140 }} className="text-center">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  No pending businesses found.
                  <div className="mt-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={fetchPending}>
                      Refresh
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row._id || row.id}>
                  <td>{row.businessName || "N/A"}</td>
                  <td>{row.branchName || "N/A"}</td>
                  <td>{row.city || "N/A"}</td>
                  <td>{row.state || "N/A"}</td>
                  <td>{row.country || "N/A"}</td>
                  <td>{row.totalEmployees ?? 0}</td>
                  <td>{row.email || "N/A"}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(row)} disabled={processing}>
                      View & Process
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={viewOpen} onClose={closeView} title="Approve / Reject Business">
        {selectedId ? (
          <>
            <div className="alert alert-info mb-3">
              <small>
                Business ID: <strong>{selectedId}</strong>
              </small>
            </div>

            <BusinessForm
              value={formDraft}
              onChange={setFormDraft}
              countryOptions={countryOptions}
              stateOptions={stateOptions}
              cityOptions={cityOptions}
              mode="edit"
            />

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <button className="btn btn-outline-secondary" onClick={closeView} disabled={processing}>
                Cancel
              </button>
              <button className="btn btn-outline-danger" onClick={handleReject} disabled={processing}>
                {processing ? "Processing..." : "Reject"}
              </button>
              <button className="btn btn-outline-success" onClick={handleApprove} disabled={processing}>
                {processing ? "Approving..." : "Approve"}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
